import React, { useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../utils/toast";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AiFillCloseCircle } from "react-icons/ai";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InputUI from "../material-ui/InputUI";
import ButtonUI from "../material-ui/ButtonUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import addUser from "../../api/addUser";
import addUserImg from "../../images/add-user.png";
import "../../styles/AddDocument.css";
import baseUrl from "../../config/baseUrl";
import { RiCloseCircleFill } from "react-icons/ri";

const addUserFormSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required field"),
  lastName: yup.string().required("Last Name is required field"),
  email: yup
    .string()
    .email("Email must be valid")
    .required("Email is required field"),
  role: yup.string().required("User Type is required field"),
  password: yup
    .string()
    .required("Password is required field")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .required("Confirm Password is required field")
    .oneOf([yup.ref("password")], "Passwords do not match"),
  userRole: yup.array().when("role", {
    is: "0",
    then: yup.array().min(1, "Role is required when user type is 'Non Admin'"),
    otherwise: yup.array(),
  }),
});

// Show / hide eye button inside a password field.
const PasswordToggle = ({ visible, onToggle }) => (
  <InputAdornment position="end">
    <IconButton
      aria-label={visible ? "Hide password" : "Show password"}
      onClick={onToggle}
      onMouseDown={(e) => e.preventDefault()}
      edge="end"
    >
      {visible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
    </IconButton>
  </InputAdornment>
);

const AddUser = ({ onAddUserButtonClick, setUserAddedToTrue }) => {
  const [loading, setLoading] = useState(false);
  const { token, user_id } = useContext(AuthContext);
  const [userRolesOptions, setUserRolesOptions] = useState([]);
  const [userType, setUserType] = useState(""); // No default value
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userRolesLoad = async () => {
    const url = `${baseUrl}/api/get-user-roles`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setUserRolesOptions(data?.data || []);
    } catch (error) {
      console.error("Error loading user roles", error);
    }
  };

  useEffect(() => {
    userRolesLoad();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(addUserFormSchema),
  });

  // Reset userRole when User Type changes
  useEffect(() => {
    setValue("userRole", []);
  }, [userType, setValue]);

  const roleOptions = [
    {
      name: "Admin",
      value: 1,
    },
    {
      name: "Non Admin",
      value: 0,
    },
  ];

  const onAddUserFormSubmit = async (addUserFormData) => {
    setLoading(true);

    const addUserFormResponse = await addUser({
      token: token,
      userId: user_id,
      first_name: addUserFormData.firstName,
      last_name: addUserFormData.lastName,
      user_email: addUserFormData.email,
      is_admin: addUserFormData.role,
      role_id: addUserFormData.userRole,
      password: addUserFormData.password,
      password_confirmation: addUserFormData.confirmPassword,
    });

    if (addUserFormResponse?.code === 200) {
      setLoading(false);
      onAddUserButtonClick();
      setUserAddedToTrue();
      showSuccess("User is added successfully");

      // Reset form after successful submission
      reset(); // This will reset the form
    } else {
      setLoading(false);
      // Server-side validation errors (e.g. password rules) come back as
      // "message" rather than "error".
      toast.error(addUserFormResponse?.error || addUserFormResponse?.message, {
        position: "top-center",
        autoClose: 5000,
        theme: "dark",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <div className="add-document-page">
      <div className="add-document-container">
        <div className="add-document-header">
          <div className="add-document-title-with-img">
            <img src={addUserImg} height="100px" width="120px" alt="Add User" />
            <div className="add-document-title">Add New User</div>
          </div>
          <ButtonUI
            formButton={false}
            variant="contained"
            buttonText={<AiFillCloseCircle />}
            style={{
              background: "none",
              padding: "0px",
              minWidth: "auto",
            }}
            onClickFunc={onAddUserButtonClick}
          />
        </div>
        <div className="add-document-form">
          <div className="add-document-inputs">
            {/* User Name Inputs */}
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="firstName"
                label="Enter First Name"
                type="text"
                variant="outlined"
                cssStyles={{
                  margin: "10px 0px",
                }}
                register={register}
              />
              <span className="error-message">
                {errors["firstName"]?.message}
              </span>
            </div>
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="lastName"
                label="Enter Last Name"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="error-message">
                {errors["lastName"]?.message}
              </span>
            </div>
          </div>
          <div className="add-document-inputs">
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="email"
                label="Enter Email ID"
                type="email"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="error-message">{errors["email"]?.message}</span>
            </div>

            {/* User Type Dropdown */}
            <div className="add-document-inputs-with-error-message">
              <FormControl fullWidth sx={{ margin: "10px 0px" }}>
                <InputLabel id="select-label">User Type</InputLabel>

                <Controller
                  control={control}
                  name="role"
                  defaultValue={"1"}
                  render={({ field }) => (
                    <Select
                      {...field} // Spread field to bind value and onChange correctly
                      labelId="select-label"
                      variant="outlined"
                      id="user-type-select"
                      onChange={(e) => {
                        const newValue = e.target.value.toString();
                        setUserType(newValue);
                        field.onChange(e);
                        if (newValue === "1") {
                          setValue("userRole", []); // Reset roles if Admin is selected
                        }
                      }}
                      label="User Type"
                    >
                      {roleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <span className="error-message">{errors["role"]?.message}</span>
            </div>
          </div>
          <div className="add-document-inputs">
            {/* Password / Confirm Password (hashed by the server on save) */}
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="password"
                label="Enter Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                autoComplete="new-password"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
                InputProps={{
                  endAdornment: (
                    <PasswordToggle
                      visible={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                    />
                  ),
                }}
              />
              <span className="error-message">
                {errors["password"]?.message}
              </span>
            </div>
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                autoComplete="new-password"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
                InputProps={{
                  endAdornment: (
                    <PasswordToggle
                      visible={showConfirmPassword}
                      onToggle={() => setShowConfirmPassword((v) => !v)}
                    />
                  ),
                }}
              />
              <span className="error-message">
                {errors["confirmPassword"]?.message}
              </span>
            </div>
          </div>
          <div className="add-document-inputs">
            {/* Roles Dropdown (Visible only when User Type is Non Admin) */}
            {userType === "0" && (
              <div
                className="add-document-inputs-with-error-message select-ui"
                style={{ width: "100%" }}
              >
                <FormControl fullWidth>
                  <InputLabel id="select-label">Roles</InputLabel>
                  <Controller
                    control={control}
                    name="userRole"
                    render={({ field }) => {
                      const { value = [], onChange } = field; // Ensure default value is always an empty array if undefined
                      const selectedRoles = value; // This will be an empty array if no roles are selected

                      // Handle dropdown selection
                      const handleSelectChange = (event) => {
                        onChange(event.target.value); // Update form state with the new selected roles
                      };

                      // Remove a tag (also deselects from dropdown)
                      const removeRole = (roleId) => {
                        const updatedRoles = selectedRoles.filter(
                          (id) => id !== roleId
                        );
                        onChange(updatedRoles); // Update form state with the new role selections
                      };

                      return (
                        <>
                          <Select
                            name="userRole"
                            labelId="select-label"
                            variant="outlined"
                            id="simple-select"
                            sx={{ margin: "10px 0px" }}
                            multiple
                            value={selectedRoles || []} // Ensure default value is an empty array if undefined
                            onChange={handleSelectChange}
                            label="Roles"
                          >
                            {userRolesOptions.map((option) => (
                              <MenuItem
                                key={option?.role_id}
                                value={option?.role_id}
                              >
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>

                          {selectedRoles.length > 0 && (
                            <div className="flex flex-wrap gap-2 p-2 bg-gray-100 rounded-b-lg rounded-none border border-gray-300">
                              {selectedRoles.map((roleId, index) => {
                                const role = userRolesOptions.find(
                                  (r) => r.role_id === roleId
                                );
                                return (
                                  <div
                                    key={index}
                                    className="flex items-center gap-1 bg-[#1a6b7c] text-white px-3 py-1 rounded-full text-sm shadow-md"
                                  >
                                    <span className="text">{role?.name}</span>
                                    <span
                                      className="close cursor-pointer hover:text-red-400 transition"
                                      onClick={() => removeRole(roleId)}
                                    >
                                      <RiCloseCircleFill className="w-4 h-4" />
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </FormControl>
                <span className="error-message">
                  {errors["userRole"]?.message}
                </span>
              </div>
            )}
          </div>
          <div className="add-document-button">
            <ButtonUI
              loading={loading}
              variant="contained"
              buttonText="Add User"
              style={{
                width: "150px",
                height: "35px",
                backgroundColor: "#1a6b7c",
                boxShadow: "none",
                border: "1px solid #1a6b7c",
                fontWeight: "500",
                borderRadius: "4px",
                letterSpacing: "0.07rem",
                marginTop: "20px",
              }}
              handleSubmit={handleSubmit}
              onFormSubmit={onAddUserFormSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
